import React from 'react';
import moment from 'moment';
import uniqWith from 'lodash/uniqWith';
import {
  CATEGORY,
  ENTITY_TYPE,
  IP_HISTORY_PAGE_SIZE,
  KRISFLYER_MILES_WEB_SCHEMA,
  UNIFORM_REFERRAL_COUNTRIES_FLAG
} from '../../utils/constants';
import { AmazeBannerData, RewardsState } from './interface';
import { historyDataType } from './HistoryInstapoints';
import { activateLoader, disableLoader } from '../../redux/actions/ui-trigger/ui-trigger-action';
import { handleError } from '../../redux/actions/common-message/message-action';
import {
  getInstapointHistory,
  getContentSchemaFromContentFul
} from '../../redux/actions/common/common-action';
import { AppDispatch } from '../../store.type';
import { getFlagValue, isFlagEnabled } from '@Redux/actions/flagsmith';

export function formatInstapointAmount(value: number | string): string {
  if (!value || value === '0' || value === 0) return '0';
  return value
    .toString()
    .replace(/^0+(?!\.)/, '')
    .replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
}

export type RewardsAction =
  | { type: 'SET_ACTIVE_TAB'; payload: 'referral' | 'instapoints' }
  | { type: 'SET_IS_MOBILE'; payload: boolean }
  | { type: 'SET_IP_LABELS'; payload: any }
  | { type: 'SET_AMAZE_BANNER_DATA'; payload: AmazeBannerData }
  | { type: 'SET_EARN_CARDS_DATA'; payload: any }
  | { type: 'SET_IP_BALANCE'; payload: Partial<RewardsState> }
  | { type: 'SET_SHARED_DATA'; payload: Partial<RewardsState> }
  | {
      type: 'SET_REFERRAL_HISTORY';
      payload: { history: RewardsState['referralHistory']; total_count: number };
    }
  | {
      type: 'SET_IP_HISTORY';
      payload: { history: RewardsState['ipHistory']; total_count: number };
    }
  | {
      type: 'APPEND_HISTORY';
      payload: { history: historyDataType[]; page: number; tab: 'referral' | 'instapoints' };
    };

export const rewardsReducer = (state: RewardsState, action: RewardsAction): RewardsState => {
  switch (action.type) {
    case 'SET_ACTIVE_TAB':
      return { ...state, activeTab: action.payload };
    case 'SET_IS_MOBILE':
      return { ...state, isMobile: action.payload };
    case 'SET_IP_LABELS':
      return { ...state, ipLabels: action.payload };
    case 'SET_AMAZE_BANNER_DATA':
      return { ...state, amazeBannerData: action.payload };
    case 'SET_EARN_CARDS_DATA':
      return { ...state, earnCardsData: action.payload };
    case 'SET_IP_BALANCE':
    case 'SET_SHARED_DATA':
      return { ...state, ...action.payload };
    case 'SET_REFERRAL_HISTORY':
      return {
        ...state,
        referralHistory: action.payload.history,
        referral_total_count: action.payload.total_count,
        referral_current_page: 1,
        referralHistoryLoaded: true
      };
    case 'SET_IP_HISTORY':
      return {
        ...state,
        ipHistory: action.payload.history,
        ip_total_count: action.payload.total_count,
        ip_current_page: 1,
        ipHistoryLoaded: true
      };
    case 'APPEND_HISTORY':
      return action.payload.tab === 'referral'
        ? {
            ...state,
            referralHistory: [...state.referralHistory, ...action.payload.history],
            referral_current_page: action.payload.page
          }
        : {
            ...state,
            ipHistory: [...state.ipHistory, ...action.payload.history],
            ip_current_page: action.payload.page
          };
    default:
      return state;
  }
};

export const fetchRewardsHistoryNextPage = async ({
  tab,
  state,
  rewardsDispatch,
  dispatch
}: {
  tab: 'referral' | 'instapoints';
  state: Pick<RewardsState, 'referral_current_page' | 'ip_current_page' | 'ipLabels'>;
  rewardsDispatch: React.Dispatch<RewardsAction>;
  dispatch: AppDispatch;
}): Promise<void> => {
  const isReferralTab = tab === 'referral';
  const currentPage = isReferralTab ? state.referral_current_page : state.ip_current_page;
  try {
    dispatch(activateLoader());
    const response = await getInstapointHistory({
      page: currentPage + 1,
      size: IP_HISTORY_PAGE_SIZE,
      revampedReferralInstapoint: isReferralTab
    });
    if (response?.data?.success) {
      const nextHistory = processHistoryDataNew(
        response.data.data.history,
        state.ipLabels,
        isReferralTab
      );
      rewardsDispatch({
        type: 'APPEND_HISTORY',
        payload: { history: nextHistory, page: currentPage + 1, tab }
      });
    }
  } catch (error) {
    dispatch(handleError(error as Error));
  } finally {
    dispatch(disableLoader());
  }
};

type IpLabels = Record<string, any>;

interface RawHistoryItem {
  entity_type: string;
  action_date: string;
  category: string;
  other_category?: string;
  instapoints: number;
  referral_instapoint_amount?: number;
  description?: string;
  expiry_date?: string;
  transaction_number: string | null;
  instapoint_transaction_number_redeem?: string;
  instapoint_transaction_number?: string;
  instapoint_transaction_number_refunded_or_reverted?: string;
  is_amaze_transaction?: boolean;
  cashback_amount?: number;
  discounted_amount?: string;
  comment?: string;
  kf_miles?: number;
  instapoints_redeem?: number;
  instapoints_refund_or_reverted?: number;
}

export const getHeading = (
  item: RawHistoryItem,
  ipLabels: IpLabels,
  isReferralContext = false
): string => {
  if (isReferralContext) {
    if (item.entity_type === ENTITY_TYPE.TRANSACTION_PENDING) {
      return ipLabels.pendingTransfer;
    }
    if (item.entity_type === ENTITY_TYPE.AMAZE_KRISFLYER_REDEMPTION) {
      return ipLabels.krisflyerMilesText;
    }
    const historyLabels = ipLabels?.uniformReferral?.history;
    if (item.category === CATEGORY.REDEEMED || item.category === CATEGORY.REVERTED) {
      return historyLabels?.referralRedeemed ?? ipLabels.referralbonus;
    }
    if (item.category === CATEGORY.EXPIRED) {
      return historyLabels?.referralExpired ?? ipLabels.referralbonus;
    }
    return historyLabels?.referralEarned ?? ipLabels.referralbonus;
  }

  const entityType = item?.entity_type;

  if (entityType === ENTITY_TYPE.ADJUSTMENT) {
    return item?.category === CATEGORY.EARNED
      ? ipLabels.creditAdjustment
      : ipLabels.debitAdjustment;
  }
  if (entityType === ENTITY_TYPE.TRANSACTION_REFUND_SETTLEMENT) {
    return item?.category === CATEGORY.REFUNDED
      ? ipLabels.refreeRefunded
      : ipLabels.referrerReverted;
  }

  const headingMap: Record<string, string> = {
    [ENTITY_TYPE.TRANSACTION_EARNED]: ipLabels.transfer,
    [ENTITY_TYPE.TRANSACTION_REDEMPTION]: ipLabels.transfer,
    [ENTITY_TYPE.PROMOTION]: ipLabels.promotion,
    [ENTITY_TYPE.EXCESS_FUND_TRANSACTION]: ipLabels.refundOnExcessAmount,
    [ENTITY_TYPE.TRANSACTION_REVERSAL]: ipLabels.reversalOnTransfer,
    [ENTITY_TYPE.AMAZE_TRANSACTION_REVERSAL]: ipLabels.reversalOnAmaze,
    [ENTITY_TYPE.PROFILE_COMPLETE_PROMOTION]: ipLabels.accountVerification,
    [ENTITY_TYPE.WELCOME]: ipLabels.signUp,
    [ENTITY_TYPE.AMAZE_TRANSACTION_EARNED]: ipLabels.amazeSpend,
    [ENTITY_TYPE.AMAZE_CASHBACK_REDEMPTION]: ipLabels.redemption,
    [ENTITY_TYPE.TRANSACTION_PENDING]: ipLabels.pendingTransfer,
    [ENTITY_TYPE.AMAZE_KRISFLYER_REDEMPTION]: ipLabels.krisflyerMilesText,
    [ENTITY_TYPE.INITIAL_REFERRAL]: ipLabels.referralbonus,
    [ENTITY_TYPE.TRANSACTION_REFERRAL]: ipLabels.referralbonus,
    [ENTITY_TYPE.INITIAL_PARTNER_REFERRAL]: ipLabels.referralbonus
  };

  return (
    headingMap[entityType] ?? (item?.category === CATEGORY.EXPIRED ? ipLabels.pointsExpiration : '')
  );
};

export const checkHistoryObjTxnNo = (
  category: string,
  item1: RawHistoryItem,
  item2: RawHistoryItem
): boolean =>
  item1.category === category &&
  item1.transaction_number !== null &&
  item2.transaction_number !== null;

export const generateHistoryObject = (
  item1: RawHistoryItem,
  ipLabels: IpLabels,
  item2?: RawHistoryItem,
  isReferralContext = false
): historyDataType => {
  const item = item2 ?? item1;
  return {
    entity_type: item?.entity_type,
    action_date: item?.action_date,
    category: item1?.category,
    other_category: item1?.other_category || item1?.category,
    creditInstapoint: isReferralContext
      ? item?.referral_instapoint_amount ?? item?.instapoints
      : item?.instapoints,
    debitInstapoint: isReferralContext
      ? Math.abs(item1?.referral_instapoint_amount ?? item1?.instapoints)
      : Math.abs(item1?.instapoints),
    description: item?.description ?? '',
    expiry_date: item?.expiry_date ?? '',
    transaction_number: item?.transaction_number ?? '',
    instapoint_transaction_number_redeem:
      item1?.instapoint_transaction_number_redeem || item1?.instapoint_transaction_number || '',
    instapoint_transaction_number: item?.instapoint_transaction_number ?? '',
    instapoint_transaction_number_refunded_or_reverted:
      item1?.instapoint_transaction_number_refunded_or_reverted ||
      item1?.instapoint_transaction_number ||
      '',
    is_amaze_transaction: item?.is_amaze_transaction || false,
    cashback_amount: item?.cashback_amount || 0,
    discounted_amount: item1?.discounted_amount || '',
    comment: item?.comment || '',
    heading: getHeading(item, ipLabels, isReferralContext),
    reverted_description: '',
    kf_miles: item?.kf_miles,
    instapoints_redeem: 0,
    instapoints_refund_or_reverted: 0
  };
};

export const processHistoryDataNew = (
  historyArray: RawHistoryItem[],
  ipLabels: IpLabels,
  isReferralContext = false
): historyDataType[] => {
  const normalizedArray = historyArray;

  let redeemRefundData = normalizedArray.filter(
    (item) =>
      (item.category === CATEGORY.REDEEMED ||
        item.category === CATEGORY.REVERTED ||
        item.category === CATEGORY.REFUNDED) &&
      item.entity_type !== ENTITY_TYPE.TRANSACTION_PENDING
  );

  redeemRefundData = uniqWith(redeemRefundData, (prev: RawHistoryItem, curr: RawHistoryItem) => {
    if (prev.transaction_number === curr.transaction_number) {
      curr.other_category = curr.category;
      curr.category = prev.category;
      curr.instapoints_redeem = prev.instapoints;
      curr.instapoints_refund_or_reverted = curr.instapoints;
      curr.instapoints = Math.abs(curr.instapoints) + Math.abs(prev.instapoints);
      curr.discounted_amount = prev.discounted_amount;
      curr.instapoint_transaction_number_redeem = prev.instapoint_transaction_number;
      curr.instapoint_transaction_number_refunded_or_reverted = curr.instapoint_transaction_number;
      return true;
    }
    return false;
  });

  const earnData = normalizedArray.filter((item1) =>
    redeemRefundData.some(
      (item2) =>
        checkHistoryObjTxnNo(CATEGORY.EARNED, item1, item2) &&
        item1.transaction_number === item2.transaction_number &&
        !item1?.description?.toLowerCase()?.includes('refund from')
    )
  );

  const referralData = normalizedArray.filter((item1) =>
    redeemRefundData.some(
      (item2) =>
        checkHistoryObjTxnNo(CATEGORY.REFERRED, item1, item2) &&
        item1.transaction_number === item2.transaction_number
    )
  );

  const earnRedeemData: historyDataType[] = [];
  const txnRevertedData: historyDataType[] = [];

  redeemRefundData.forEach((item1) => {
    referralData.forEach((item2) => {
      if (item1.transaction_number === item2.transaction_number) {
        const obj = generateHistoryObject(item1, ipLabels, item2, isReferralContext);
        obj.category = item2.category;
        obj.reverted_description = item1?.description ?? '';
        obj.instapoint_transaction_number_refunded_or_reverted =
          item1?.instapoint_transaction_number ?? '';
        obj.instapoints_refund_or_reverted = Math.abs(
          item1.instapoints_refund_or_reverted || item1.instapoints
        );
        txnRevertedData.push(obj);
      }
    });
  });

  redeemRefundData.forEach((item1) =>
    earnData.forEach((item2) => {
      if (item1?.transaction_number === item2?.transaction_number) {
        const obj = generateHistoryObject(item1, ipLabels, item2, isReferralContext);
        obj.instapoints_redeem = Math.abs(item1.instapoints_redeem || obj.debitInstapoint);
        obj.instapoints_refund_or_reverted = Math.abs(
          item1.instapoints_refund_or_reverted || item1.instapoints
        );
        obj.reverted_description = item1?.description ?? '';
        earnRedeemData.push(obj);
      }
    })
  );

  const tempArray = [...earnData, ...txnRevertedData];
  const tempRestData = normalizedArray.filter(
    (item1) =>
      !tempArray.some(
        (item2) =>
          item1?.transaction_number !== null &&
          item2?.transaction_number !== null &&
          item2?.transaction_number === item1.transaction_number &&
          item1?.entity_type !== ENTITY_TYPE.TRANSACTION_PENDING &&
          !item1?.description?.toLowerCase()?.includes('refund from')
      )
  );

  const restData = tempRestData.map((item) => {
    const obj = generateHistoryObject(item, ipLabels, undefined, isReferralContext);
    const pointsValue = isReferralContext
      ? item?.referral_instapoint_amount ?? item?.instapoints
      : item?.instapoints;
    obj.creditInstapoint =
      item?.category === CATEGORY.EARNED ||
      item?.category === CATEGORY.REFERRED ||
      (item?.category === CATEGORY.REVERSED &&
        item?.entity_type === ENTITY_TYPE.TRANSACTION_REVERSAL)
        ? pointsValue
        : 0;
    obj.debitInstapoint =
      item?.category === CATEGORY.REDEEMED ||
      item?.category === CATEGORY.EXPIRED ||
      item?.category === CATEGORY.REFUNDED ||
      item?.category === CATEGORY.REVERTED ||
      (item?.category === CATEGORY.REVERSED &&
        item?.entity_type === ENTITY_TYPE.AMAZE_TRANSACTION_REVERSAL)
        ? Math.abs(pointsValue)
        : 0;
    return obj;
  });

  return [...earnRedeemData, ...txnRevertedData, ...restData].sort(
    (a, b) => Number(moment(b?.action_date)) - Number(moment(a?.action_date))
  );
};

export const fetchIpHistoryNextPage = async ({
  state,
  setState,
  dispatch
}: {
  state: { current_page_number: number; ipLabels: Record<string, any>; history: historyDataType[] };
  setState: (updater: (prev: any) => any) => void;
  dispatch: AppDispatch;
}): Promise<void> => {
  try {
    dispatch(activateLoader());
    const response = await getInstapointHistory({
      page: state.current_page_number + 1,
      size: IP_HISTORY_PAGE_SIZE
    });
    if (response?.data?.success) {
      let { history } = response.data.data;
      history = processHistoryDataNew(history, state.ipLabels);
      setState((prev: any) => ({
        ...prev,
        history: [...prev.history, ...history],
        current_page_number: prev.current_page_number + 1
      }));
    }
  } catch (error) {
    dispatch(handleError(error as Error));
    // eslint-disable-next-line no-console
    console.error(error);
  } finally {
    dispatch(disableLoader());
  }
};

export const fetchKrisFlyerDataFromCMS = async ({
  setState,
  isAmazeUser
}: {
  setState: (updater: (prev: RewardsState) => RewardsState) => void;
  isAmazeUser: boolean;
}): Promise<void> => {
  const data = await getContentSchemaFromContentFul(
    'componentMessageCentreItem',
    'KRISFLYER_BANNER'
  );
  setState((prev: RewardsState) => ({
    ...prev,
    amazeBannerData: parseKrisFlyerBannerData(data, isAmazeUser)
  }));
};

export const parseAmazeBannerData = (raw: any): AmazeBannerData => ({
  bannerHeading: raw?.banner_heading?.value,
  bannerDescription: raw?.bannerDescription?.value,
  bannerImgWebView: raw?.bannerImgWebView?.target?.fields?.url,
  bannerImgMobileView: raw?.bannerImgMobileView?.target?.fields?.url,
  btnLabel: raw?.btn?.target?.fields?.label?.value
});

export const parseKrisFlyerBannerData = (raw: any, isAmazeUser: boolean): AmazeBannerData => {
  const data = raw?.styleJson?.value;
  return {
    bannerHeading: isAmazeUser
      ? data?.bannerHeading || KRISFLYER_MILES_WEB_SCHEMA.bannerHeading
      : data?.bannerHeading2 || KRISFLYER_MILES_WEB_SCHEMA.bannerHeading2,
    bannerDescription: data?.bannerDescription || KRISFLYER_MILES_WEB_SCHEMA.bannerDescription,
    bannerImgWebView: raw?.cardImg?.value[0]?.target?.fields?.url,
    bannerImgMobileView: raw?.cardImg?.value[1]?.target?.fields?.url,
    btnLabel: data?.btnLabel || KRISFLYER_MILES_WEB_SCHEMA.btnLabel
  };
};

export const parseEarnCardsData = (earnCardsList: any[]): any[] | null => {
  const processed = earnCardsList?.map((card) => {
    const fields = card?.target?.fields;
    const imgItem = fields?.carouselImage?.value?.find(
      (i: any) => i?.target?.fields?.title === 'EARN_CARD_IMG_WEB'
    );
    return {
      cardImgUrl: imgItem?.target?.fields?.url,
      cardHeading: fields?.heading?.value,
      cardDescription: fields?.description?.value,
      btnLabel: fields?.btn?.target?.fields?.label?.value,
      redirectLink: fields?.btn?.target?.fields?.redirectLinkWeb?.value,
      supportingCountries: fields?.supportingCountries?.value,
      earnType: fields?.earnType?.value
    };
  });
  return processed?.length ? processed : null;
};
export const isUniformReferralCountry = (countryCode: string): boolean => {
  const key = UNIFORM_REFERRAL_COUNTRIES_FLAG;
  if (!isFlagEnabled(key)) return false;

  const data = getFlagValue(key);
  // only show for countries added if empty dont show for any country"
  return Array.isArray(data) && data.includes(countryCode);
};
